import type { Tool } from "@/hooks/types";

/**
 * The vocabularies the tool lists narrow by, and what each value admits.
 *
 * Two lists ask the same questions of the same documents — the group's
 * catalogue and a business unit's tool management page — and both had grown past the
 * point where scrolling works (Jira NAV-85). Keeping the filters here rather
 * than in each page means "covered" cannot come to mean one thing on one screen
 * and something else on the other, and it is a module a test can hold, which a
 * lazy route component is not.
 *
 * Each filter is a `Record` from its value to a predicate rather than a list,
 * and that is deliberate: a list annotated with the value type type-checks
 * while *missing* a member — so a new value would silently stop being offered
 * — and a dropdown value with no predicate would silently match everything.
 * The same trick `availability.ts` uses for access sources, for the same
 * reason.
 */

/**
 * Whether a group entry names any business unit that may adopt it.
 *
 * Not "is this tool available" — the group states no availability (ADR 0012).
 * What a group entry says is which business units may take the tool up, and naming
 * nobody is an ordinary state: the group knows the tool and no contract names a
 * business unit yet.
 *
 * **The words a reader sees say "which business units may adopt this", and deliberately
 * borrow neither "contract" nor "availability"** (Jira NAV-109). Both were
 * already taken by the two things this must not be confused with: on the same
 * screen "group contract" is a pipeline with its own states — awaiting review,
 * in negotiation, contracted, declined — and "availability" is a *business unit's* own
 * position on a tool, which decides how a request for it is routed. The
 * identifiers here still say `coverage`, because the field genuinely is set by
 * contracts and because `?coverage=COVERED` is an address people have already
 * shared; what changed is the copy, and the column beside it changed with it so
 * the two cannot read as different axes.
 *
 * **Deliberately the raw field, ungated by `groupContractStatus`** — unlike the
 * two product hooks, which count coverage only once a contract is `CONTRACTED`
 * because they answer "who is paying for this separately". This filter answers a
 * different question: it narrows the group catalogue by *what the column beside
 * it shows*, and that column lists `coveredBusinessUnitIds` unconditionally,
 * because filling the list in is the super admin's authoring act and happens
 * before Legal signs anything. Gating here would put the filter and the cell
 * beside it in disagreement, which is worse than the distinction it would
 * enforce — so the labels say "named" rather than "covered by", and a tool in
 * negotiation reads as naming business units here while the product pages still call it
 * a candidate.
 */
export const COVERAGE_FILTERS = ["COVERED", "UNCOVERED"] as const;
export type CoverageFilter = (typeof COVERAGE_FILTERS)[number];

export const MATCHES_COVERAGE: Record<
  CoverageFilter,
  (tool: Pick<Tool, "coveredBusinessUnitIds">) => boolean
> = {
  COVERED: (tool) => (tool.coveredBusinessUnitIds ?? []).length > 0,
  UNCOVERED: (tool) => (tool.coveredBusinessUnitIds ?? []).length === 0,
};

/**
 * Whose catalogue entry a row is — the group's, or the business unit's own.
 *
 * Worth filtering by because it decides what the business unit can do with the row: its
 * own entries are editable and the group's are read-only, so "show me the ones
 * I can change" is a real question about a long list.
 */
export const ENTRY_OWNERS = ["GROUP", "OWN"] as const;
export type EntryOwnerFilter = (typeof ENTRY_OWNERS)[number];

export const MATCHES_ENTRY_OWNER: Record<
  EntryOwnerFilter,
  (tool: Pick<Tool, "isGlobal">) => boolean
> = {
  // A document written before the BU-scoped split counts as the group's, the
  // same fallback `useTools` applies when it scopes a list.
  GROUP: (tool) => tool.isGlobal !== false,
  OWN: (tool) => tool.isGlobal === false,
};

/**
 * Does the search box match this tool?
 *
 * Name, category and vendor: the three things a row prints that somebody might
 * be searching for. The description is deliberately not searched — it is a
 * sentence, so it matches nearly any short word and turns the box into a way of
 * getting the whole list back.
 *
 * An empty needle matches everything, so a caller can pass whatever is in the
 * box without checking it first. Trimmed here rather than by each caller: the
 * box itself must keep whatever was typed (see `textParam`).
 */
export function matchesToolText(
  tool: Pick<Tool, "name" | "category" | "vendor">,
  needle: string,
): boolean {
  const term = needle.trim().toLowerCase();
  if (!term) return true;
  return [tool.name, tool.category, tool.vendor ?? ""].some((field) =>
    field.toLowerCase().includes(term),
  );
}

/**
 * The categories a list actually holds, sorted, for its category dropdown.
 *
 * Derived from the rows rather than from the categories collection on purpose:
 * a dropdown offering a category no row has is a choice whose only outcome is
 * an empty list, and the group's category list is longer than any one business unit's
 * catalogue uses.
 */
export function categoryOptionsOf(
  tools: readonly Pick<Tool, "category">[],
): string[] {
  return [
    ...new Set(tools.map((tool) => tool.category).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
}

/**
 * Does the category filter admit this tool? Several categories at once, and the
 * only reading of several that means anything (Jira NAV-109).
 *
 * A tool has **one** category, so "and" would select nothing and there is
 * nothing for an any/all switch to choose between — which is the one thing this
 * does not copy from the multi-BU control on the group's product view, where
 * both readings are real questions. So the selection means "any of these",
 * silently and always, and nothing on screen offers a mode.
 *
 * An empty selection matches everything, so a caller can pass whatever the
 * control holds without checking it first — the same contract
 * {@link matchesToolText} keeps for an empty needle.
 */
export function matchesToolCategories(
  tool: Pick<Tool, "category">,
  categories: readonly string[],
): boolean {
  return categories.length === 0 || categories.includes(tool.category);
}
